import type { Material } from '@draughts/shared';
import { SLAB_GLOSS_ID } from './material-defs-ids';

/** I due file di venatura di un materiale: uno per parità di casella / colore di pezzo. */
export interface MaterialTextures {
  /** Caselle CHIARE e pezzi chiari. */
  light: string;
  /** Caselle SCURE e pezzi scuri. */
  dark: string;
}

/**
 * I quattro file di venatura: RASTER COTTI, in `public/textures/`.
 *
 * ═══ PERCHE' NON SONO PIU' SVG ══════════════════════════════════════════════════
 *
 * Fino alla 1.13.24 questi erano i sorgenti SVG stessi, e qui c'era scritto: «il browser
 * li rasterizza una volta per dimensione d'uso e li tiene in cache, quindi il costo del
 * filtro non tocca mai il percorso per-frame».
 *
 * Era una scommessa, ed è stata persa. Chromium mette in cache il PAINT RECORD
 * dell'immagine SVG — parsing e layout non si ripetono — non il suo raster: i filtri
 * diventano `SkImageFilter` dentro quel record, ed è la rasterizzazione a eseguirli. Il
 * legno è un grafo di ~40 primitive (7-8 `feTurbulence`, 3 `feDisplacementMap`) su una
 * regione di 1536x1536 unità, e ce ne sono quattro copie per velatura, per due velature,
 * dentro il buffer di una maschera.
 *
 * ═══ DOVE STA IL COSTO, misurato ═══════════════════════════════════════════════
 *
 * NON nel trascinamento. Misurando quello non si vede niente: a regime il tassello del
 * pattern è già in cache e sorgente e cotto costano uguale entro il 5%. È il motivo per
 * cui il difetto è stato difficile da inquadrare, e per cui la prima diagnosi — «il
 * filtro viene rieseguito a ogni frame» — era più forte del vero.
 *
 * Il costo sta nella PRIMA rasterizzazione, e si ripaga da capo a ogni dimensione nuova:
 * apertura, ricarica, rotazione, ridimensionamento della finestra, barra dell'indirizzo
 * che si ritrae, e ogni schermata che monta una scacchiera di lato diverso (replay,
 * editor, anteprima dei problemi, anteprima delle impostazioni). Finché dura, i thread di
 * raster sono saturi — ed è lì che il trascinamento va a scatti e il telefono scotta.
 *
 * `scripts/measure-slab-raster.mjs`, CPU dell'intero albero di processi, mediana,
 * `data:` URL e nessuna intercettazione di rotta (intercettare costa da solo un terzo
 * della CPU: verificato sulla PLASTICA, che non scarica texture affatto e che con
 * l'intercettazione attiva mostrava la stessa differenza del legno — un confronto fatto
 * così misura lo strumento, non l'oggetto):
 *
 *   scenario                                  legno vivo/cotto   marmo vivo/cotto
 *   primo paint, telefono 360 dsf3                       0.91x              0.88x
 *   primo paint, 768 dsf2                                1.31x              0.98x
 *   primo paint, desktop 1440 dsf2                       3.51x              1.92x
 *   sei cambi di dimensione, telefono 360 dsf3           1.75x              1.63x
 *   trascinamento a regime                               1.00x              1.00x
 *
 * Su desktop il legno vivo costava 12,7 secondi di CPU contro 3,6 per aprire una
 * scacchiera: nove secondi di lavoro in più, e cresce col quadrato della dimensione.
 *
 * L'UNICA RIGA IN CUI IL COTTO PERDE è il primo paint su schermo piccolo, dove
 * decodificare un WebP da 1024² costa un 10% più che eseguire il filtro su un telo da
 * 540px. Si rientra al primo cambio di dimensione — che su un telefono arriva da solo —
 * e da lì in poi il raster è gratis mentre il filtro ricomincia ogni volta.
 *
 * I sorgenti vivono ora in `textures-src/`, FUORI dalla cartella servita, e da lì si
 * cuociono con `pnpm --filter @draughts/web bake-textures`. Restano la fonte di verità e
 * restano il posto dove si tocca la venatura, ma non arrivano più a un browser. Stessa
 * filosofia di `variants/variants.jsonc` -> `pnpm codegen:variants`.
 *
 * ═══ IL PREZZO, dichiarato ══════════════════════════════════════════════════════
 *
 * Non più ~700 B gzip l'uno ma ~300 KB, ed è il motivo per cui NON sono più precache-ati:
 * `webp` non è nei `globPatterns` di vite.config.ts, e una regola `runtimeCaching`
 * CacheFirst li prende al primo uso. Il materiale di default è la plastica, quindi il
 * precache di CHI NON LI USA cala di 56 KB, e chi sceglie legno o marmo paga due file una
 * volta sola. Il degrado resta pulito: se un file non c'è, l'`<image>` non dipinge e la
 * scacchiera resta ai suoi colori (vedi `BoardSlab`).
 *
 * ═══ RISOLUZIONE E CODIFICA, scelte con una misura ══════════════════════════════
 *
 * 1024², WebP `nearLossless` a q=40. `scripts/compare-texture.mjs` compone raster e
 * sorgente come li compone l'app — la velatura sopra un colore di casella vero,
 * all'opacità del materiale — e ne misura la distanza in Oklab alle dimensioni di DEVICE
 * a cui la scacchiera li usa davvero. Il dE medio sta fra 0.001 e 0.004 e il massimo sotto
 * 0.05, contro lo 0.06 che il repo usa già come soglia di «questo segno SI DISTINGUE»
 * (board-highlight-contrast.test.ts).
 *
 * Perché 1024 e non meno: il vincolo NON è il legno — il cui telo è di `grainSquares`
 * caselle, cioè ~800 px di device nel caso peggiore — ma il MARMO, che con
 * `grainSquares: null` stende un telo unico su tutta la lastra e arriva a chiederne 1600.
 * A 512 il suo dE massimo raddoppia (0.035 contro 0.013).
 *
 * Perché `nearLossless` e non lossy: il file dipinge RGB in {0, 255} con alpha variabile,
 * e dove alpha vale 0 il canale RGB è INDEFINITO. Un encoder lossy YUV codifica quella
 * spazzatura e la lascia sbordare nei pixel a bassa alpha vicini — aloni grigi esattamente
 * sui bordi delle vene. `nearLossless` è invece una pre-elaborazione dentro la modalità
 * lossless: misurato, a q=40 dà lo stesso dE del lossless puro riga per riga, e pesa il
 * 35% di meno.
 *
 * Il percorso è assoluto dalla radice, come per i suoni in `utils/audio.ts`: in questo
 * pacchetto non si importano asset dal bundler, si servono da `public/`.
 */
export const MATERIAL_TEXTURES = {
  wood: { light: '/textures/wood-light.webp', dark: '/textures/wood-dark.webp' },
  marble: { light: '/textures/marble-light.webp', dark: '/textures/marble-dark.webp' },
} as const satisfies Record<string, MaterialTextures>;

/**
 * Le stesse venature a 128², per le PASTIGLIE del pannello Grafica e basta.
 *
 * Esistono per una ragione sola: le quattro voci del selettore mostrano tutti e quattro i
 * materiali insieme, e senza queste aprire «Grafica» tirerebbe giù un megabyte e mezzo per
 * disegnare quattro quadratini da 26px. Sono cotte a 128 DAL SORGENTE e non ricampionate
 * dal 1024, perché il picco di questi file dipende dalla scala.
 *
 * NON le usa l'anteprima grande (`PreferencesPreview`), che ha caselle da 56 unità e
 * mostra un telo di cinque: quella deve mostrare la venatura vera, ed è comunque un
 * materiale solo, quello già scelto.
 */
export const MATERIAL_TEXTURES_PREVIEW = {
  wood: { light: '/textures/wood-light-128.webp', dark: '/textures/wood-dark-128.webp' },
  marble: { light: '/textures/marble-light-128.webp', dark: '/textures/marble-dark-128.webp' },
} as const satisfies Record<string, MaterialTextures>;

/**
 * Finestra `[x, y, w, h]` sul file (che è 1024x1024 UNITÀ) usata dalla venatura dei PEZZI.
 *
 * UNITÀ, non pixel, e da quando i file sono raster cotti la distinzione conta: il
 * `viewBox` del pattern in `MaterialDefs` e l'`<image width="1024" height="1024">` che
 * contiene vivono nello spazio utente dell'SVG, indipendente dalla risoluzione del raster.
 * È per questo che ricuocere a una risoluzione diversa non tocca una riga né di
 * `MaterialDefs` né di `BoardSlab`, e che i numeri qui sotto restano validi alla lettera.
 *
 * Centrata, così ruotarla di 90° attorno al proprio centro la lascia dov'è — è l'unica
 * cosa che `board-materials.test.ts` fissa, insieme al fatto che sia quadrata.
 *
 * QUANTO INGRANDISCE, e contro cosa. Il ritaglio ingrandisce 4x rispetto a stendere il
 * file intero sul pezzo, ma il pezzo resta più piccolo del telo e il conto che conta è
 * quello: sul telo del legno 1024 unità coprono ~161px (0.157 px/unità, vedi
 * `grainSquares`), sul pezzo 256 unità ne coprono ~26 (0.10 px/unità). La venatura sul
 * pezzo è quindi ~1.5 volte più fine che sulla casella — e non più ~3, perché il telo si è
 * stretto e la casella si è avvicinata al pezzo. `piece-lod.ts` la spegne comunque sotto i
 * 22px: la soglia è sul pezzo, e il pezzo non è cambiato.
 *
 * Era 384 e non 256: stringerla è ciò che porta la riga di legno tardivo da 0.7px a 1.0px
 * sul pezzo più piccolo che ancora la disegna. Non si stringe oltre perché la finestra è
 * UNA per tutti i materiali, e sotto le ~200 unità il marmo — che ha una vena ogni ~243 —
 * finirebbe con pezzi senza nessuna vena dentro.
 */
export const PIECE_GRAIN_WINDOW = [384, 384, 256, 256] as const;

export interface BoardMaterialSpec {
  /** I due file di venatura, o `null` per un materiale che non ne ha. */
  textures: MaterialTextures | null;
  /**
   * Gli stessi due file a 128², per le pastiglie del pannello Grafica.
   *
   * Sta nello spec e non in un `if` dentro `BoardSlab` perché è una proprietà del
   * MATERIALE — «di questa venatura esiste anche una miniatura» — e perché così un
   * materiale senza venatura non ha modo di dichiararne una per sbaglio: `null` in
   * entrambi i campi o in nessuno, ed è un test a tenerlo vero.
   */
  texturesPreview: MaterialTextures | null;
  /**
   * Lato di UN telo di venatura, in CASELLE. `null` = il telo è la scacchiera intera.
   *
   * È la scala della venatura, ed è espressa in caselle perché la casella è l'unica unità
   * che tutti i chiamanti di `BoardSlab` condividono: la scacchiera vera ha caselle da 90
   * unità, la replay da 60, l'editor da 40, l'anteprima delle impostazioni da 56 e la
   * pastiglia da 13. Ancorare il telo alla LASTRA — che è ciò che si faceva prima, e che i
   * commenti dei file SVG danno ancora per scontato — significa che quegli stessi file
   * cadono su 2 caselle nella pastiglia, 4 nell'anteprima e 10 sulla scacchiera: la stessa
   * venatura ingrandita tre volte e mezzo a seconda di dove la si guarda, e un'anteprima
   * che mostra una cosa diversa da quella che poi si gioca.
   *
   * QUANTO VALE 5, misurato sui due estremi che contano:
   *
   *   telefono 360px, 10x10   casella ~32px   telo ~161px   anello ~6,3px
   *   desktop, casella 60px                   telo  300px   anello ~12px
   *
   * Il bersaglio era l'anteprima di oggi, che su telefono rende l'anello a ~6,9px ed è la
   * resa che convince; prima la scacchiera lo rendeva a ~12,6px su telefono e ~23px su
   * desktop.
   *
   * IL NUMERO DA GUARDARE PER PRIMO se la venatura degrada: la FIBRA, che ha ~18 unità di
   * passo trasversale, cioè ~2,8px su telefono. È il segno più fine dei due file ed è anche
   * il protagonista — è per la fibra che la superficie si legge come legno — quindi è il
   * primo a soffrire e il primo da guardare. Alzare `grainSquares` a 6 o 7 gli dà aria, ed
   * è un numero solo.
   */
  grainSquares: number | null;
  /** Paint di un overlay UNIFORME. Solo il vetro: è un riflesso, non una venatura. */
  fill: string | null;
  /** Opacità dell'overlay. Vedi l'invariante qui sotto. */
  opacity: number;
  /**
   * Alpha NERA massima che i due file possono produrre — il pixel più scuro che
   * l'overlay è capace di dipingere.
   *
   * È MISURATA, non dedotta: dedurla dalle tabelle dei file dà una stima troppo alta,
   * perché assume che i due rami scuri (anello e poro nel legno, vena e nuvola nel
   * marmo) si sovrappongano, e in pratica non capita mai. Si rimisura disegnando la
   * texture su fondo bianco e leggendo il pixel più scuro; i valori qui sotto tengono
   * un margine sulla misura. `board-materials.test.ts` li usa per l'invariante.
   */
  darkPeak: number;
}

/**
 * INVARIANTE: `opacity * darkPeak <= MAX_LOCAL_DARKENING`.
 *
 * Il tetto NON è sull'opacità totale, ed è qui che questo commento corregge quello che
 * c'era prima. La lastra vecchia era un overlay in prevalenza SCURO, quindi ogni punto
 * di opacità era un punto di annerimento e 0.10 era il tetto sul totale. I file nuovi
 * dipingono in NERO E IN BIANCO su fondo trasparente, con i due rami disgiunti e di
 * media quasi uguale: la luminanza MEDIA della casella non si sposta.
 *
 * Il bianco per giunta non costa: schiarendo il fondo l'aiuto di mossa GUADAGNA
 * contrasto (`out = (1-a)*base + a` contro `out = (1-a)*base + a*hint`) — verificato su
 * tutte e dodici le palette, migliora ovunque. Il vincolo è solo sul nero, ed è LOCALE.
 *
 * QUANTO COSTA IL NERO, misurato (contrasto WCAG del pallino `rgba(44,95,45,0.45)` a
 * `opacity: 0.55`, cioè alpha effettiva 0.2475, sulla propria casella scura):
 *
 *   tema             L casella   nudo    nero@0.10   delta
 *   mahogany           0.055     1.019     1.048     +0.029
 *   walnut             0.092     1.041     1.006     -0.035
 *   slate              0.134     1.081     1.041     -0.040
 *   high-contrast      0.156     1.111     1.069     -0.042
 *   purple-lavender    0.164     1.140     1.096     -0.044
 *   ocean              0.241     1.194     1.147     -0.047
 *   blue-grey..sand   .24-.35   1.20-1.28 1.15-1.23  -0.049
 *
 * Due conseguenze, entrambe diverse da quel che il codice dichiarava prima:
 *
 * 1. `ocean` NON è il caso limite: è a metà classifica. Il fondo più scuro è `mahogany`,
 *    dove il pallino è già oggi a 1.02 di contrasto — cioè illeggibile — e dove il nero
 *    semmai AIUTA, perché lì il verde è più chiaro della casella. È un difetto
 *    preesistente di board-themes.ts, non di questa lastra.
 * 2. Il prezzo massimo del nero al tetto è -0.049 di contrasto, su qualunque tema. È per
 *    questo che la venatura può ora essere quattro volte più marcata di prima senza
 *    spendere più di quanto spendesse il vecchio 0.10: quel che costa è solo il nero, e
 *    il nero è rimasto dov'era.
 */
export const MAX_LOCAL_DARKENING = 0.1;

export const BOARD_MATERIALS: Record<Material, BoardMaterialSpec> = {
  // Misurato con `node scripts/measure-texture.mjs`: wood-light 0.196, wood-dark 0.286 —
  // ed è il massimo dei due a comandare, dichiarato 0.31 con margine. Nero effettivo
  // 0.28*0.31 = 0.087, cioè un filo MENO dei 0.088 di prima.
  //
  // È il risultato di come i due file sono stati riscritti: prima quasi tutto il contrasto
  // stava in un segno solo — l'anello, picco 0.302 — e l'opacità era tenuta a 0.16 proprio
  // perché quel segno era nero. Ora lo stesso carattere è distribuito su quattro strati
  // tenui (fibra, fasce, anelli, tono) che sono campi indipendenti e non si sovrappongono
  // quasi mai: il picco quasi dimezza, e l'opacità può salire da 0.16 a 0.28 spendendo
  // comunque un filo meno nero di prima. La venatura ha più corpo E gli aiuti di mossa
  // stanno un capello meglio — non è un compromesso, è lo stesso budget speso meglio.
  //
  // Il picco NON dipende da `grainSquares`: misurato a 161 e a 322px dà le stesse cifre,
  // perché la sfocatura è dichiarata in unità di file e rimpicciolisce con tutto il resto.
  // Cambiare la scala del telo non richiede di rimisurare questi due numeri.
  wood: {
    textures: MATERIAL_TEXTURES.wood,
    texturesPreview: MATERIAL_TEXTURES_PREVIEW.wood,
    grainSquares: 5,
    fill: null,
    opacity: 0.28,
    darkPeak: 0.31,
  },
  // Misurato: marble-light 0.369, marble-dark 0.486. Il marmo può permettersi
  // un'opacità più alta del legno proprio perché il suo nero è più raro e meno
  // profondo, ed è quel che gli serve: la vena è tutto quello che ha da mostrare.
  // Nero effettivo 0.19*0.50 = 0.095.
  // `grainSquares: null`: il marmo resta ancorato alla LASTRA, cioè al comportamento
  // storico. È una scelta, non una dimenticanza — una lastra di marmo è un pezzo unico e
  // la sua vena non ha ragione di ripetersi ogni cinque caselle — e il prezzo è che la sua
  // anteprima continua a mostrarlo più fitto di quanto la scacchiera lo renderà.
  marble: {
    textures: MATERIAL_TEXTURES.marble,
    texturesPreview: MATERIAL_TEXTURES_PREVIEW.marble,
    grainSquares: null,
    fill: null,
    opacity: 0.19,
    darkPeak: 0.5,
  },
  // Il vetro non ha venatura: è trasparente, e quel che si vede è il riflesso — sulla
  // lastra questa banda di luce, sui pezzi il riflesso di ciascuno (vedi `Piece`).
  glass: {
    textures: null,
    texturesPreview: null,
    grainSquares: null,
    fill: `url(#${SLAB_GLOSS_ID})`,
    opacity: 0.09,
    darkPeak: 0.22,
  },
  // Opaca: nessun overlay. È deliberatamente il materiale "pulito", quello che rende
  // la scacchiera esattamente come era prima che i materiali esistessero — ed è per
  // questo che è il default (prima voce di MATERIALS).
  plastic: { textures: null, texturesPreview: null, grainSquares: null, fill: null, opacity: 0, darkPeak: 0 },
};

/** Lo spec del materiale, con ripiego sulla plastica se il valore salvato non esiste più. */
export function resolveBoardMaterial(material: Material): BoardMaterialSpec {
  return BOARD_MATERIALS[material] ?? BOARD_MATERIALS.plastic;
}

/**
 * INCIDENZA sulla lastra: distanza radiale normalizzata del centro di una casella dal
 * centro della scacchiera, 0 al centro e 1 sugli angoli.
 *
 * Serve al riflesso dei pezzi sul vetro (vedi `Piece`): il vetro specchia tanto più quanto
 * lo sguardo è radente, quindi al centro della lastra — dove la camera guarda quasi a
 * perpendicolo — il riflesso è debole, e verso i bordi è più marcato e un po' più lungo.
 *
 * Il denominatore NON è il semilato: nessun pezzo sta sul bordo, il centro di casella più
 * lontano dista `(size - squareSize) / 2` per asse, e normalizzare su quello è ciò che fa
 * arrivare a 1 esatto un pezzo d'angolo.
 *
 * INVARIANTE che risparmia un ramo a ogni chiamante: è INVARIANTE PER ROTAZIONE DI 180°.
 * Quindi non serve distinguere il caso `flipped`, né dove il ribaltamento è la rotazione CSS
 * dell'intero SVG (`GameBoard`) né dove sono le coordinate a essere specchiate (`GameReplay`,
 * `EditorBoard`, `PuzzlePreviewBoard`).
 */
export function glassIncidence(
  x: number,
  y: number,
  size: number,
  squareSize: number,
): number {
  const half = size / 2;
  const maxOffset = (size - squareSize) / 2;
  if (maxOffset <= 0) return 0;
  return Math.min(1, Math.hypot(x - half, y - half) / (maxOffset * Math.SQRT2));
}
